import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Recovery draft: a copy of an unsaved form kept in THIS browser only, so a crash,
 * a reload or an accidental close does not lose a half-built invoice.
 *
 *   const draft = useRecoveryDraft({ userId, invoiceId, meta });   // invoiceId null = a new invoice
 *   draft.recovered          // { payload, savedAt, meta } found on open, or null
 *   draft.schedule(form)     // debounced write (1s) on every edit
 *   draft.flush()            // write the pending value now (explicit "keep on this device")
 *   draft.clear()            // remove it (after a real save, or an explicit discard)
 *
 * This is NOT autosave of money to the server (decision D3): nothing here talks to
 * the API. It is a convenience, so it is allowed to fail quietly:
 * - every `localStorage` call is in try/catch (private windows, blocked site data and
 *   thumbnail capture all throw); the page works the same without it, and
 *   `storageOk` goes false so the UI never claims a recovery copy that was not written;
 * - a draft older than 7 days is dropped on read, and stale ones are swept on mount;
 * - the key carries the user id AND the invoice id, so one seller's draft is never
 *   offered to another login on the same device, nor one invoice's to another.
 *
 * `meta` is a small JSON-able note stored BESIDE the payload with every write and handed back as
 * `recovered.meta` (the invoice builder stores the `updated_at` the copy was typed against, so a
 * restored copy can still be refused by the server's stale-tab check). It is read at write time, so
 * the latest value always goes with the payload.
 *
 * The stored shape is versioned. v2 added `meta`; a v1 copy (no `meta`) is still read, and comes
 * back with no `meta`. Anything else is not ours and is dropped.
 *
 * A write that is still PENDING (a timer not yet fired) when the key changes or the dialog goes
 * away is CANCELLED, never flushed here: a discard clears the draft and a late timer re-creating it
 * would resurrect what the seller threw away. The ones that must survive an unmount are written by
 * the OWNER, explicitly (`flush`), before it goes (see `useInvoiceEditor`'s pagehide / unmount flush).
 */

export const RECOVERY_DRAFT_PREFIX = 'craftflow:invoice-recovery';
export const RECOVERY_DRAFT_DEBOUNCE_MS = 1000;
export const RECOVERY_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const VERSION = 2;
// Versions this build can still read: a copy written before `meta` existed must restore.
const READABLE_VERSIONS = [1, 2];

export function recoveryDraftKey(userId, invoiceId) {
  return `${RECOVERY_DRAFT_PREFIX}:${userId}:${invoiceId ?? 'new'}`;
}

function readStored(key, now) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Not our JSON: fall through to the malformed branch below.
    }
    const savedAt = Number(parsed && parsed.savedAt);
    const wellFormed =
      parsed && READABLE_VERSIONS.includes(parsed.v) && Number.isFinite(savedAt) && 'payload' in parsed;
    if (!wellFormed || now - savedAt > RECOVERY_DRAFT_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    const found = { payload: parsed.payload, savedAt };
    // `meta` only when the copy carries one (a v1 copy does not), so the shape stays what it was.
    if (parsed.meta && typeof parsed.meta === 'object') found.meta = parsed.meta;
    return found;
  } catch {
    // Storage blocked: treat as "no draft".
    return null;
  }
}

// Remove every expired recovery draft on this device, whoever owns it.
function sweepExpired(now) {
  try {
    const stale = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(`${RECOVERY_DRAFT_PREFIX}:`)) stale.push(key);
    }
    // readStored removes what is expired or malformed and keeps the rest.
    stale.forEach(key => readStored(key, now));
  } catch {
    // Nothing to sweep, or storage is unavailable.
  }
}

// Remove every recovery draft on this device, whoever owns it. Called at logout: a copy holds a
// customer's email, name, notes and line items in plaintext, and the next person to sign in on a
// shared machine must not inherit it (the key's user id only stops it being OFFERED to them).
export function clearAllRecoveryDrafts() {
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(`${RECOVERY_DRAFT_PREFIX}:`)) keys.push(key);
    }
    keys.forEach(key => window.localStorage.removeItem(key));
  } catch {
    // Storage blocked: nothing was written, so there is nothing to remove.
  }
}

export default function useRecoveryDraft({ userId, invoiceId = null, enabled = true, meta = null } = {}) {
  const active = Boolean(enabled && userId);
  const storageKey = active ? recoveryDraftKey(userId, invoiceId) : null;

  const [state, setState] = useState(() => ({
    key: storageKey,
    recovered: storageKey ? readStored(storageKey, Date.now()) : null,
  }));
  // The key changed (another invoice opened in the same mounted dialog): re-read.
  if (state.key !== storageKey) {
    setState({ key: storageKey, recovered: storageKey ? readStored(storageKey, Date.now()) : null });
  }

  const [storageOk, setStorageOk] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const timerRef = useRef(null);
  const pendingRef = useRef({ has: false, payload: null });
  const keyRef = useRef(storageKey);
  keyRef.current = storageKey;
  const metaRef = useRef(meta);
  metaRef.current = meta;

  const cancelTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const write = useCallback(() => {
    const key = keyRef.current;
    if (!key || !pendingRef.current.has) return false;
    const { payload } = pendingRef.current;
    pendingRef.current = { has: false, payload: null };
    const savedAt = Date.now();
    try {
      const record = { v: VERSION, savedAt, payload };
      if (metaRef.current) record.meta = metaRef.current;
      window.localStorage.setItem(key, JSON.stringify(record));
      setStorageOk(true);
      setLastSavedAt(savedAt);
      return true;
    } catch {
      // Quota or blocked storage: say so instead of pretending a copy exists.
      setStorageOk(false);
      return false;
    }
  }, []);

  const schedule = useCallback(
    payload => {
      if (!keyRef.current) return;
      pendingRef.current = { has: true, payload };
      cancelTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        write();
      }, RECOVERY_DRAFT_DEBOUNCE_MS);
    },
    [cancelTimer, write]
  );

  // Write the latest scheduled value now. With an argument, write that value instead.
  const flush = useCallback(
    payload => {
      if (!keyRef.current) return false;
      if (payload !== undefined) pendingRef.current = { has: true, payload };
      cancelTimer();
      return write();
    },
    [cancelTimer, write]
  );

  const clear = useCallback(() => {
    cancelTimer();
    pendingRef.current = { has: false, payload: null };
    const key = keyRef.current;
    setState(prev => (prev.recovered ? { ...prev, recovered: null } : prev));
    setLastSavedAt(null);
    if (!key) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Nothing was written if storage is blocked, so there is nothing to remove.
    }
  }, [cancelTimer]);

  // Cancel (never flush) a pending write when the key changes or the dialog goes away.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingRef.current = { has: false, payload: null };
    };
  }, [storageKey]);

  useEffect(() => {
    if (active) sweepExpired(Date.now());
  }, [active]);

  return {
    recovered: state.recovered,
    storageOk,
    lastSavedAt,
    schedule,
    flush,
    clear,
  };
}
